import { useEffect, useState } from 'react';
import { WbGrid, WbGridCol, WbGridRow, WbIcon, WbText } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import Exterior360View from './Exterior360View';
import Interior360View from './Interior360View';
import { checkImage } from '../../../Utils/Vimdt/checkImage';
import { compareTitle } from '../../../Utils/Vimdt/compareTitle';
import './Image360ViewContainer.scss';

const imageHelper = async (images, key) => {
	const promiseList = images
		.filter((image) => image.title.includes(key))
		.sort(compareTitle)
		.map((image) => checkImage(image.url));

	const settled = await Promise.allSettled(promiseList);
	return settled
		.filter((element) => element.status === 'fulfilled')
		.map((element) => element.value.path);
};

function Image360ViewContainer({ images, currentVin }) {
	const { t } = useTranslation();
	const [interiorImages, setInteriorImages] = useState([]);
	const [externalImages, setExternalImages] = useState([]);

	const [showExterior, setShowExterior] = useState(true);
	const [exteriorResetDisabled, setExteriorResetDisabled] = useState(false);
	const [showInterior, setShowInterior] = useState(true);
	const [interiorResetDisabled, setInteriorResetDisabled] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const source = images[currentVin] || [];
			const [intImages, extImages] = await Promise.all([
				imageHelper(source, 'BIS'),
				imageHelper(source, 'EXT'),
			]);
			if (cancelled) return;
			setInteriorImages(intImages);
			setExternalImages(extImages);
		})();
		return () => {
			cancelled = true;
		};
	}, [currentVin, images]);

	// Remounting the viewer is the only way to reset its internal rotation state.
	const remount = (setShow, setDisabled) => {
		setShow(false);
		setDisabled(true);
		setTimeout(() => setShow(true), 500);
		setTimeout(() => setDisabled(false), 2000);
	};

	const section = (heading, disabled, onReset, viewer) => (
		<WbGridCol mq1={12}>
			<div className="vimdt-360__heading">
				<WbText size="l">{heading}</WbText>
				<button
					type="button"
					aria-label={heading}
					className="vimdt-360__reset"
					disabled={disabled}
					onClick={onReset}
				>
					<WbIcon name="bds/reset/24" />
				</button>
			</div>
			{viewer}
		</WbGridCol>
	);

	return (
		<WbGrid fullWidth className="vimdt-360">
			<WbGridRow>
				{externalImages.length >= 4 &&
					section(
						t('vimdt.imagesTab.image360View.headingExterior'),
						exteriorResetDisabled,
						() => remount(setShowExterior, setExteriorResetDisabled),
						showExterior && <Exterior360View images={externalImages} />
					)}
				{interiorImages.length === 6 &&
					section(
						t('vimdt.imagesTab.image360View.headingInterior'),
						interiorResetDisabled,
						() => remount(setShowInterior, setInteriorResetDisabled),
						showInterior && <Interior360View images={interiorImages} />
					)}
			</WbGridRow>
		</WbGrid>
	);
}

export default Image360ViewContainer;
